import { useState, useEffect } from 'react';
import { RefreshCw, Server, Wifi, Globe } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { tenantService } from '../services';
import type { SharedGateway } from '../services/tenant.service';
import { useAuthStore } from '../stores/authStore';
import { useTenantStore } from '../stores/tenantStore';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import StatCard from '../components/common/StatCard';

const TenantAdminSharedGateways = () => {
  const { user } = useAuthStore();
  const { selectedTenantId } = useTenantStore();
  const { getDateFormatSettings } = useSettingsStore();
  const [gateways, setGateways] = useState<SharedGateway[]>([]);
  const [loading, setLoading] = useState(false);

  // TenantAdmin's own tenant, else the topbar selection (SuperAdmin and
  // ChannelAdmin have no tenant of their own). The prior SuperAdmin-only split
  // left a ChannelAdmin with an empty tenant and no gateways.
  const tenantId = user?.tenantId || selectedTenantId || '';

  const fetchGateways = async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const data = await tenantService.getSharedGateways(tenantId);
      setGateways(data);
    } catch (err) {
      console.error('Failed to fetch shared gateways:', err);
      setGateways([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGateways();
  }, [tenantId]);

  const dateSettings = getDateFormatSettings();

  const columns: Column<SharedGateway>[] = [
    {
      key: 'name',
      label: 'Gateway Name',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <Server className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'active' ? 'Online' : row.status === 'provisioned' ? 'Provisioned' : 'Offline'}
        />
      ),
    },
    {
      key: 'onboardStatus',
      label: 'Onboard Status',
      render: (row) => (
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
          row.onboardStatus === 'active' ? 'bg-green-100 text-green-800' :
          row.onboardStatus === 'provisioning' ? 'bg-yellow-100 text-yellow-800' :
          row.onboardStatus === 'pending' ? 'bg-gray-100 text-gray-800' :
          'bg-red-100 text-red-800'
        }`}>
          {row.onboardStatus}
        </span>
      ),
    },
    {
      key: 'tunnelProtocol',
      label: 'Protocol',
      render: (row) => (
        <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
          {row.tunnelProtocol === 'ikev2' ? 'IKEv2' : 'WireGuard'}
        </span>
      ),
    },
    {
      key: 'wanIp',
      label: 'WAN IP',
      render: (row) => (
        <span className="text-sm font-mono text-gray-700">
          {row.wanIp || <span className="text-gray-400">N/A</span>}
        </span>
      ),
    },
    {
      key: 'internetBreakout',
      label: 'Internet Breakout',
      render: (row) => (
        <span className={`text-xs font-medium ${row.internetBreakout ? 'text-green-700' : 'text-gray-400'}`}>
          {row.internetBreakout ? 'Enabled' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.lastSeen ? formatDate(row.lastSeen, dateSettings) : 'Never'}
        </span>
      ),
    },
  ];

  const onlineCount = gateways.filter(g => g.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Shared Gateways</h1>
          <p className="mt-1 text-gray-600">
            Multi-tenant gateway edges assigned to your tenant
          </p>
        </div>
        <button
          onClick={fetchGateways}
          disabled={loading}
          className="btn-secondary inline-flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={Server} title="Total Gateways" value={gateways.length} color="primary" />
        <StatCard icon={Wifi} title="Online" value={onlineCount} color="success" />
        <StatCard icon={Globe} title="Internet Breakout" value={gateways.filter(g => g.internetBreakout).length} color="info" />
      </div>

      {/* Table */}
      <div className="card">
        <DataTable
          columns={columns}
          data={gateways}
          loading={loading}
          emptyMessage="No shared gateways assigned to your tenant."
        />
      </div>
    </div>
  );
};

export default TenantAdminSharedGateways;
